import { Spin, Tag } from 'antd';
import { Plus, RefreshCw } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { Empty } from '@/components/common/Empty';
import { SearchBar } from '@/components/common/SearchBar';
import { Button } from '@/components/ui/button';
import { applicationStore, brandStore } from '@/stores';
import type { AppCatalogAppItem } from '@/types';
import { isNextopBrand } from '@/utils/brand';
import { AppFormDialog } from './components/AppFormDialog';
import { ApplicationTable } from './components/ApplicationTable';
import { VersionDrawer } from './components/VersionDrawer';
import { APP_STATUS_OPTIONS } from './constants';

export const ApplicationManagementPage = observer(() => {
  const [appDialogOpen, setAppDialogOpen] = useState(false);
  const [editingApp, setEditingApp] = useState<AppCatalogAppItem | null>(null);
  const activeBrand = brandStore.activeBrand;
  const available = isNextopBrand(activeBrand);

  useEffect(() => {
    if (available) {
      void applicationStore.fetchApps().catch(() => undefined);
    }
  }, [available]);

  const handleSearch = useCallback((keyword: string) => {
    applicationStore.setAppsKeyword(keyword);
  }, []);

  const openCreateDialog = () => {
    setEditingApp(null);
    setAppDialogOpen(true);
  };

  const openEditDialog = (app: AppCatalogAppItem) => {
    setEditingApp(app);
    setAppDialogOpen(true);
  };

  const counts = applicationStore.appCounts;

  if (!available) {
    return (
      <div className="bg-white rounded-lg border border-border shadow-sm">
        <div className="p-6 space-y-6">
          <div className="space-y-1">
            <h1 className="text-lg font-semibold text-foreground">应用管理</h1>
            <p className="text-sm text-muted-foreground">
              该能力仅适用于 Nextop Brand。
            </p>
          </div>
          <Empty description="请切换到 Nextop Brand 后再管理应用目录和版本。" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-border shadow-sm">
      <div className="p-6 space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-1">
            <h1 className="text-lg font-semibold text-foreground">应用管理</h1>
            <p className="text-sm text-muted-foreground">
              管理 Nextop App Center 应用目录、版本发布和 latest 指针。
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => void applicationStore.fetchApps()}
              loading={applicationStore.appsLoading}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              刷新
            </Button>
            <Button onClick={openCreateDialog}>
              <Plus className="mr-2 h-4 w-4" />
              新增应用
            </Button>
          </div>
        </div>

        <Spin spinning={applicationStore.appsLoading && counts.total === 0}>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
            <div className="rounded-md border border-border px-4 py-3">
              <div className="text-xs text-muted-foreground">总数</div>
              <div className="mt-1 text-2xl font-semibold">{counts.total}</div>
            </div>
            {APP_STATUS_OPTIONS.map((status) => (
              <div
                key={status.value}
                className="rounded-md border border-border px-4 py-3"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-muted-foreground">
                    {status.label}
                  </span>
                  <Tag color={status.color} className="m-0">
                    {status.value}
                  </Tag>
                </div>
                <div className="mt-1 text-2xl font-semibold">
                  {counts[status.value]}
                </div>
              </div>
            ))}
          </div>
        </Spin>

        <SearchBar
          placeholder="搜索 App ID、名称、分类或状态..."
          onSearch={handleSearch}
          className="max-w-md"
        />

        <ApplicationTable onEdit={openEditDialog} />
      </div>

      <AppFormDialog
        open={appDialogOpen}
        app={editingApp}
        onOpenChange={(open) => {
          setAppDialogOpen(open);
          if (!open) {
            setEditingApp(null);
          }
        }}
      />

      <VersionDrawer />
    </div>
  );
});
